import { first, sortBy, isEmpty } from 'lodash'
import moment from 'moment'
import produce from 'immer'

import { getAllProductsAtQuays, getGridTime, getMatchingProducts } from './general'
import { checkChainCargo, determineTotalDurationDelays, updateCargoEnd, getMinMaxBollards } from './terminalVisitUtils'
import { QUAY_ROLES } from '../js/constants'

/**
 * Prepares the terminal visits to use
 * @param {TerminalVisit[]} terminalVisits
 * @param {Config} config
 */
export function getTerminalVisitsDataset(terminalVisits, config) {
  const allProducts = getAllProductsAtQuays(config.quays)

  return terminalVisits.map(visit => {
    const totalDurationDelays = determineTotalDurationDelays(visit)

    const orders = visit.orders
      .filter(order => {
        return order.cargoStart != null && order.cargoEnd && order.bollardStart != null && order.bollardEnd != null
      })
      .map(order => {
        const matchingProduct = getMatchingProducts(order.product, order.operation, allProducts)

        return {
          ...order,
          color: matchingProduct.length ? matchingProduct[0].color : '#C0C0C0',
          timeIsChained: checkChainCargo(order, config, totalDurationDelays)
        }
      })

    return {
      ...visit,
      orders,
      zIndex: 0
    }
  })
}

/**
 * The same as getTerminalVisitsDataset but for a single terminal visit
 * @param {TerminalVisit} terminalVisit
 * @param {Config} config
 */
export function getTerminalVisitDataset(terminalVisit, config) {
  return first(getTerminalVisitsDataset([terminalVisit], config))
}

/**
 * Based on the terminalVisit and the given quays, determine if the visit is on a external quay.
 * @param {TerminalVisit} terminalVisit
 * @param {Quay[]} quays
 */
export function isVisitAtExternalQuay(terminalVisit, quays) {
  if (terminalVisit && quays) {
    const terminalVisitQuay = quays.find(quay => quay.name === terminalVisit.quayId)
    if (terminalVisitQuay && terminalVisitQuay.role === 'EXTERNAL') {
      return true
    } else {
      return false
    }
  } else {
    return false
  }
}

// helper function to sort terminal visits:
// - regular visits before board-board visits
// - ordered by startTime
export function sortTerminalVisits(terminalVisits) {
  return sortBy(terminalVisits, [item => (item.boardBoard ? 1 : 0), 'startTime'])
}

/**
 * Changes TerminalVisit items based on dragging time/bollard positions
 * @param {TerminalVisitItem[]} visitsToUpdate Visits to update
 * @param {TerminalVisitItem} updatedItem Updated TerminalVisit item param
 * @param {number} diffTime difference between current and previous time position
 * @param {number} diffBollard difference between current and previous bollard position
 * @param {Config} config
 * @param {boolean} updatedVisitIsParallel Whether or not visit is parallel
 * @returns {TerminalVisitItem[]} updated terminal visit items
 */
export function updateTerminalVisits(
  visitsToUpdate,
  updatedItem,
  diffTime,
  diffBollard,
  config,
  updatedVisitIsParallel
) {
  return visitsToUpdate.map(visit => {
    return produce(visit, draftVisit => {
      // for parallel visit, both orders should be moved together
      const ordersToEdit = updatedVisitIsParallel
        ? draftVisit.referenceTerminalVisit.orders
        : draftVisit.referenceTerminalVisit.orders.slice(draftVisit.orderIndex - 1)

      // Ensure consistent order spacing even when order duration changes because of non-working hours
      const timeBetweenOrders = ordersToEdit.map(({ cargoStart }, index) =>
        index > 0 ? cargoStart - ordersToEdit[index - 1].cargoEnd : 0
      )
      ordersToEdit.forEach((order, index) => {
        const previousOrder = ordersToEdit[index - 1]

        const totalDurationDelays = determineTotalDurationDelays(updatedItem.referenceTerminalVisit)
        // TODO: Figure out what this magic function does.
        const chained = checkChainCargo(order, config, totalDurationDelays)

        // keep order spacing consistent
        if (previousOrder) {
          order.cargoStart = getGridTime(moment(previousOrder.cargoEnd + timeBetweenOrders[index]))
        } else {
          order.cargoStart = getGridTime(moment(order.cargoStart).add(diffTime, 'ms'))
        }

        // if the order is chained, order duration should also account for non-working hours
        if (chained) {
          order = updateCargoEnd(order, config, totalDurationDelays)
        } else {
          order.cargoEnd = getGridTime(moment(order.cargoEnd).add(diffTime, 'ms'))
        }

        // diffBollard expected to be 0 if dragging moves item outside quay boundaries
        if (order) {
          order.bollardStart += diffBollard
          order.bollardEnd += diffBollard
        }
      })
      return draftVisit
    })
  })
}

/**
 * Changes TerminalVisit items based on status and times of orders
 * @param {TerminalVisit} visitToUpdate Visits to update
 * @param {Config} config
 * @returns {TerminalVisit} updated terminal visit items
 */
export function shiftOrMatchOrdersWithTerminalVisit(visitToUpdate, config) {
  return produce(visitToUpdate, draftVisit => {
    const ordersToEdit = draftVisit.orders

    // Ensure consistent order spacing even when order duration changes because of non-working hours
    const timeBetweenOrders = draftVisit.orders.map(({ cargoStart }, index) =>
      index > 0 ? cargoStart - ordersToEdit[index - 1].cargoEnd : 0
    )
    ordersToEdit.forEach((order, index) => {
      const previousOrder = ordersToEdit[index - 1]

      const totalDurationDelays = determineTotalDurationDelays(draftVisit)
      // TODO: Figure out what this magic function does.
      const chained = checkChainCargo(order, config, totalDurationDelays)

      // keep order spacing consistent
      if (previousOrder) {
        order.cargoStart = previousOrder.cargoEnd + timeBetweenOrders[index]
      }

      // if the order is chained, order duration should also account for non-working hours
      if (chained) {
        order = updateCargoEnd(order, config, totalDurationDelays)
      }
    })
    return draftVisit
  })
}

export function categorizeVisits(quays = [], terminalVisits = []) {
  // filters out any quays which have EXTERNAL / LAYBY Type
  const allExternalQuays = quays.filter(quay => quay.role === QUAY_ROLES.external)
  // always get the first of all the external quays. For now we only support 1 external quay. We can change this later on.
  const externalQuayToUse = allExternalQuays ? allExternalQuays[0] : undefined

  // make a distrinction between the visits on the main quay and the visits on the external quay
  // get the visits that are placed on the currently active external quay ( should at the moment only be one quay )
  const externalQuayVisits = externalQuayToUse
    ? terminalVisits.filter(visit => visit.quayId === externalQuayToUse.name)
    : []
  // get the visits that are not on the external quay ( if the external quay exists )
  const mainQuayVisits = externalQuayToUse
    ? terminalVisits.filter(visit => visit.quayId !== externalQuayToUse.name)
    : terminalVisits

  return {
    externalQuayVisits,
    mainQuayVisits
  }
}

export function isVisitOnExternalQuay(quays, selectedQuayId) {
  if (isEmpty(quays) || isEmpty(selectedQuayId)) {
    return false
  }
  const selectedVisitQuay = quays.find(quay => quay.name === selectedQuayId)
  // figure out if the visit is on a EXTERNAL QUAY
  const isOnExternalQuay = selectedVisitQuay ? selectedVisitQuay.role === QUAY_ROLES.external : false

  return isOnExternalQuay
}

/**
 * Checks that no selected visit will be dragged outside the boundaries of the current quay.
 * @param{number} diffBollard bollard dragging difference from TerminalViewer component
 * @param {TerminalVisitItem[]} visitsBeingUpdated
 * @param {Config} config
 * @returns {boolean} false if applying diffBollard to selected visits drags any of them outside the boundaries of the quay
 */
export const checkIfBollardPositionShouldUpdate = (diffBollard, visitsBeingUpdated, config) => {
  const { minBollard, maxBollard } = getMinMaxBollards(config.quays)

  for (const visit of visitsBeingUpdated) {
    for (const order of visit.referenceTerminalVisit.orders) {
      if (
        (diffBollard > 0 && order.bollardEnd + diffBollard > maxBollard) || // dragging right
        (diffBollard < 0 && order.bollardStart + diffBollard < minBollard) // dragging left
      ) {
        return false
      }
    }
  }

  return true
}
